import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { api } from "@/lib/api";
import { toast, Toaster } from "sonner";
import { Lock, FileText, Download } from "lucide-react";

export default function LegalGenerator() {
  const [data, setData] = useState(null);
  const [ack, setAck] = useState(false);
  const [tab, setTab] = useState("privacy");
  const [locked, setLocked] = useState(false);

  useEffect(() => {
    api.get("/legal/templates").then(({ data }) => setData(data)).catch(() => setLocked(true));
  }, []);

  if (locked || !data) {
    return (
      <Layout>
        <section className="max-w-3xl mx-auto px-6 py-16 text-center">
          <Lock className="w-12 h-12 text-[var(--rg-sage)] mx-auto mb-3" />
          <h1 className="font-heading font-bold text-2xl text-[var(--rg-text-strong)] mb-2">Legal Page Generator is a Pro feature</h1>
          <Link to="/billing"><Button className="bg-[var(--rg-sage)] text-[var(--rg-on-sage)] hover:bg-[var(--rg-sage-hover)] font-bold">Upgrade to Pro</Button></Link>
        </section>
      </Layout>
    );
  }

  const current = tab === "privacy" ? data.privacy_policy : data.terms_of_service;

  const download = () => {
    if (!ack) { toast.error("You must acknowledge the disclaimer before downloading"); return; }
    const blob = new Blob([current], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `${tab === "privacy" ? "privacy-policy" : "terms-of-service"}-DRAFT.txt`; a.click();
  };

  return (
    <Layout>
      <Toaster theme="dark" position="top-right" offset={80} />
      <section className="max-w-5xl mx-auto px-6 py-10">
        <div className="text-[10px] uppercase tracking-[0.24em] font-bold text-[var(--rg-sage)] mb-3">Legal Page Generator · Pro</div>
        <h1 className="font-heading font-extrabold text-3xl text-[var(--rg-text-strong)] mb-3">Draft Legal Templates</h1>

        <div className="rounded-md border-2 border-[var(--rg-red)] bg-[var(--rg-red-soft)] p-5 mb-6 text-center" data-testid="legal-watermark">
          <div className="font-heading font-extrabold text-[var(--rg-red)] text-2xl">{data.watermark}</div>
          <div className="text-sm text-[var(--rg-text)] mt-1">{data.disclaimer}</div>
        </div>

        <div className="flex gap-2 mb-4">
          <Button variant={tab === "privacy" ? "default" : "outline"} onClick={() => setTab("privacy")} className={tab === "privacy" ? "bg-[var(--rg-sage)] text-[var(--rg-on-sage)] hover:bg-[var(--rg-sage-hover)]" : "border-[var(--rg-border)] text-[var(--rg-text-strong)] hover:bg-[var(--rg-surface)]"} data-testid="tab-privacy">
            Privacy Policy
          </Button>
          <Button variant={tab === "tos" ? "default" : "outline"} onClick={() => setTab("tos")} className={tab === "tos" ? "bg-[var(--rg-sage)] text-[var(--rg-on-sage)] hover:bg-[var(--rg-sage-hover)]" : "border-[var(--rg-border)] text-[var(--rg-text-strong)] hover:bg-[var(--rg-surface)]"} data-testid="tab-tos">
            Terms of Service
          </Button>
        </div>

        <div className="rounded-lg border border-[var(--rg-border)] bg-[var(--rg-surface)] p-5 relative">
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <div className="text-[var(--rg-red)]/15 font-heading font-extrabold text-6xl rotate-[-20deg] whitespace-nowrap">DRAFT — NOT LEGAL ADVICE</div>
          </div>
          <pre className="relative whitespace-pre-wrap text-sm text-[var(--rg-text)] font-mono leading-relaxed max-h-[480px] overflow-y-auto">{current}</pre>
        </div>

        <label className="flex items-start gap-3 mt-5 cursor-pointer" data-testid="legal-ack-checkbox">
          <Checkbox checked={ack} onCheckedChange={setAck} className="border-[var(--rg-border-hi)] mt-0.5" />
          <span className="text-sm text-[var(--rg-text)]">
            I acknowledge this is a DRAFT TEMPLATE and is not legal advice. I will have it reviewed by a licensed attorney before publishing.
          </span>
        </label>

        <div className="mt-5">
          <Button onClick={download} disabled={!ack} className="bg-[var(--rg-sage)] text-[var(--rg-on-sage)] hover:bg-[var(--rg-sage-hover)] font-semibold" data-testid="legal-download">
            <Download className="w-4 h-4 mr-2" /> Download {tab === "privacy" ? "Privacy Policy" : "Terms of Service"} Draft
          </Button>
        </div>
      </section>
    </Layout>
  );
}
